import { addToast, Modal, Button } from '@octano/global-ui';
import { Container, Col, Row } from 'reactstrap';
import {
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  Ref,
} from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { CardTitle } from '../../../../../components/text';
import { ClassroomEventFormFields, CreateClassroomEventDto } from '../types';
import ClassroomEventForm from './ClassroomEventForm';
import { TimeModuleType } from '../../../../../types/timeModuleTypes';
import { createClassroomEvent } from '../../api';
import { useTexts } from '../hooks/useTexts';

export type ClassroomEventModalMethods = {
  open: () => void;
  close: () => void;
};

type ClassroomEventModalProps = {
  classroomId: number;
  modules: TimeModuleType[];
  onSave?: (eventDate?: string) => void;
};

const DEFAULT_VALUES: ClassroomEventFormFields = {
  title: '',
  startDate: null,
  endDate: null,
  startModule: null,
  endModule: null,
  description: '',
};

const ClassroomEventModal = (
  { classroomId, modules, onSave }: ClassroomEventModalProps,
  ref: Ref<ClassroomEventModalMethods>,
) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const texts = useTexts();

  const form = useForm<ClassroomEventFormFields>({
    defaultValues: DEFAULT_VALUES,
  });

  const open = useCallback(() => {
    form.reset(DEFAULT_VALUES);
    setVisible(true);
  }, [form]);

  const close = useCallback(() => {
    setVisible(false);
    form.reset(DEFAULT_VALUES);
  }, [form]);

  const handleClose = useCallback(() => {
    if (saving) {
      return;
    }
    close();
  }, [close, saving]);

  const handleCreate = useCallback(
    (payload: CreateClassroomEventDto) => {
      if (saving) {
        return Promise.resolve();
      }
      setSaving(true);
      return createClassroomEvent(payload)
        .then(({ error, status }) => {
          if (error) {
            if (status === 409) {
              // El backend detalla QUÉ fechas del rango están ocupadas; si por
              // algo no llega, cae al mensaje genérico.
              const detalle = (error as any)?.error?.response?.data?.message;
              return addToast({
                icon: 'error',
                color: 'danger',
                text:
                  typeof detalle === 'string' && detalle
                    ? detalle
                    : texts.form.conflictError,
              });
            }
            return addToast({
              icon: 'error',
              color: 'danger',
              text: texts.form.error,
            });
          }

          addToast({
            icon: 'success',
            color: 'success',
            text: texts.form.success,
          });

          close();
          !!onSave && onSave(payload.startDate);
        })
        .finally(() => {
          setSaving(false);
        });
    },
    [saving, close, onSave, texts],
  );

  const handleSubmit = useCallback(
    (values: ClassroomEventFormFields) => {
      if (
        !values.startModule ||
        !values.endModule ||
        !values.startDate ||
        !values.endDate
      ) {
        return addToast({
          icon: 'error',
          color: 'danger',
          text: texts.form.error,
        });
      }
      if (values.startModule.value > values.endModule.value) {
        return addToast({
          icon: 'error',
          color: 'danger',
          text: texts.form.modulesError,
        });
      }

      const payload: CreateClassroomEventDto = {
        title: values.title,
        startDate: values.startDate,
        endDate: values.endDate,
        startModuleId: values.startModule.value,
        endModuleId: values.endModule.value,
        classroomId,
        ...(values.description && { description: values.description }),
      };

      return handleCreate(payload);
    },
    [classroomId, handleCreate, texts],
  );

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  return (
    <Modal isOpen={visible} size="lg" toggle={handleClose} unmountOnClose>
      <FormProvider {...form}>
        <Container>
          <Row>
            <Col xs={12}>
              <div className="text-center">
                <CardTitle children={texts.form.cardTitle} />
              </div>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <p className="text-center fs-16 mx-2">
                {texts.form.cardSubtitle}
              </p>
            </Col>
          </Row>
        </Container>
        <ClassroomEventForm modules={modules} />
        <Container>
          <Row className="pt-5">
            <Col xs={12} lg={6} className="pb-2 order-2 order-lg-1">
              <Button
                type="button"
                outlined
                disabled={saving}
                onClick={handleClose}
                text={texts.form.cancel}
                fullwidth
              />
            </Col>
            <Col xs={12} lg={6} className="pb-2 order-1 order-lg-2">
              <Button
                type="button"
                loading={saving}
                text={texts.form.submit}
                onClick={form.handleSubmit(handleSubmit)}
                fullwidth
              />
            </Col>
          </Row>
        </Container>
      </FormProvider>
    </Modal>
  );
};

export default forwardRef(ClassroomEventModal);
